import React, { useState, useEffect } from 'react';
import { Package, Calendar, User, GitBranch, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const ReleaseManagement = () => {
  const { user } = useAuth();
  const [releases, setReleases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRelease, setNewRelease] = useState({
    version: '',
    notes: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    loadReleases();
  }, []);

  const loadReleases = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/releases/`);
      setReleases(response.data);
    } catch (error) {
      console.error('Error loading releases:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRelease = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/releases/`,
        newRelease,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      alert('Release created successfully!');
      setShowCreateModal(false);
      setNewRelease({ version: '', notes: '' });
      loadReleases();
    } catch (error) {
      console.error('Error creating release:', error);
      alert('Failed to create release: ' + (error.response?.data?.detail || error.message));
    }
  };

  const generateVersionSuggestion = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    // Count releases today
    const today = `${year}-${month}-${day}`;
    const todayReleases = releases.filter(r => r.deployed_at.startsWith(today));
    const buildNumber = todayReleases.length + 1;
    
    return `v${year}.${month}.${day}.${buildNumber}`;
  };

  if (isLoading) {
    return <div className="p-6">Loading releases...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Release Management</h1>
          <p className="text-gray-600">Track deployment versions and release history</p>
        </div>
        {user?.role === 'admin' && (
          <Button 
            onClick={() => {
              setNewRelease({ ...newRelease, version: generateVersionSuggestion() });
              setShowCreateModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Release
          </Button>
        )}
      </div>

      {/* Create Release Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Create New Release</h2>
            
            <div className="space-y-4">
              <div>
                <Label>Version Number</Label>
                <Input
                  value={newRelease.version}
                  onChange={(e) => setNewRelease({ ...newRelease, version: e.target.value })}
                  placeholder="v2024.10.21.1"
                />
                <p className="text-xs text-gray-500 mt-1">Suggested: {generateVersionSuggestion()}</p>
              </div>
              
              <div>
                <Label>Release Notes</Label>
                <textarea
                  value={newRelease.notes}
                  onChange={(e) => setNewRelease({ ...newRelease, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="4"
                  placeholder="What's new in this release..."
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                onClick={handleCreateRelease}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Create Release
              </Button>
              <Button
                onClick={() => setShowCreateModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Release History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Release History</h2>
        </div>
        
        <div className="divide-y">
          {releases.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No releases yet. Create your first release to start tracking deployments.
            </div>
          ) : (
            releases.map((release) => (
              <div key={release.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-blue-600" />
                        <span className="font-bold text-lg">{release.version}</span>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        {release.environment}
                      </span>
                    </div>
                    
                    {release.notes && (
                      <p className="text-gray-700 mb-2">{release.notes}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(release.deployed_at).toLocaleString()}
                      </div>
                      {release.deployed_by && (
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {release.deployed_by}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ReleaseManagement;
